import { ContentTree } from './data-structure/content-tree';
import { FirstAlignArea } from './data-structure/first-align-area';
import { TextLeafNode } from './data-structure/text-leaf-node';
import { VTitleTemplate } from './data-structure/vertical-title-template';
import {
    PageText,
    PageType,
} from '@src/slide/application/service/slide-content-layout-analyzer/core/algorithm/data-structure/page-content';
import { IPresentationModel } from '@src/slide/domain/ui-model/element-modeles/Interface/presentation-model.interface';

export class PageContentTextResult {
    public texts: TextLeafNode[];
    public pageType;
}

export class TextAnalyzer {
    // 得到提取的文本信息
    public analysisTitleInfo(
        contentTreeList: Array<ContentTree>,
        vTitleTemplateMap: Map<VTitleTemplate, Array<FirstAlignArea>>,
        vTitleTemplateSimilarityMap: Map<VTitleTemplate, Array<number>>,
    ): TextLeafNode[][] {
        const vIndexTitleTemplateMap = this.getVIndexTitleTemplateMap(vTitleTemplateMap, vTitleTemplateSimilarityMap);
        return contentTreeList.map((contentTree, index) => {
            const vTitleTemplate: VTitleTemplate | undefined = vIndexTitleTemplateMap.get(index);
            return this.getShowResultFromContentTree(contentTree, vTitleTemplate);
        });
    }


    // 根据内容推测
    // eslint-disable-next-line complexity
    public analysisPageInfoByContent(
        pageTexts: PageText[],
        presentation: IPresentationModel,
        titleResult: {
            titleTemplateMap: Map<VTitleTemplate, Array<FirstAlignArea>>,
            titleTemplateSimilarMap: Map<VTitleTemplate, Array<number>>,
        },
    ) {
        if (pageTexts.length === 0) return;
        // 提取有用信息
        let tablesPageIndex = -1; // 目录页下标
        let coverPageIndex = -1; // 封面页下标
        // eslint-disable-next-line complexity
        pageTexts.forEach((currentPageText, index) => {
            if (index === 0) {
                const placeHolders = currentPageText.pageModel.getAllChildrenOfPlaceHolder();
                if (placeHolders.find(placeholder => placeholder.isCtrTitlePl)) {
                    // 第一页并且有主标题，认为是首页
                    pageTexts[index].pageType = PageType.CoverPage;
                    coverPageIndex = index;
                }
            }
            if (tablesPageIndex === -1) {
                // 目录页
                for (let i = 0; i < currentPageText.texts.length; i++) {
                    const lowerText = currentPageText.texts[i].text.toLowerCase();
                    if (this.isContentsRef(lowerText) && lowerText.length < 10) {
                        currentPageText.pageType = PageType.TableOfContents;
                        tablesPageIndex = index;
                        break;
                    }
                }
                if (this.isContentsListRef(currentPageText.texts.map(textNode => textNode.text))) {
                    currentPageText.pageType = PageType.TableOfContents;
                    tablesPageIndex = index;
                }
            }
            // 感谢
            for (let i = 0; i < currentPageText.texts.length; i++) {
                if (currentPageText.texts[i].text.toLowerCase().includes('thank')
                    || currentPageText.texts[i].text.includes('谢谢')
                    || currentPageText.texts[i].text.includes('感谢')
                ) {
                    currentPageText.pageType = PageType.Acknowledgments;
                }
            }
        });
        if (pageTexts.length > 1) {
            if (tablesPageIndex === 1) {
                // 目录页在第二页，推测第一页是标题页面
                pageTexts[0].pageType = PageType.CoverPage;
                coverPageIndex = 0;
            }
        }
        if (tablesPageIndex !== -1) {
            this.handleHasTableContent(pageTexts, tablesPageIndex, titleResult);
        } else {
            this.handleNoTableContent(pageTexts, titleResult);
        }
        if (pageTexts[0].pageType === PageType.Content) {
            pageTexts[0].pageType = PageType.CoverPage;
            coverPageIndex = 0;
        }
        if (coverPageIndex !== -1) {
            pageTexts[coverPageIndex].resetCoverPageTitle();
        }
    }

    private getShowResultFromContentTree(contentTree: ContentTree, vTitleTemplate?: VTitleTemplate): TextLeafNode[] {
        const textTree = contentTree.getTextTree();
        if (!textTree) return [];
        if (textTree.length === 0) return [];
        if (!textTree[0]) return [];
        if (textTree[0] instanceof TextLeafNode) {
            if (vTitleTemplate) {
                textTree[0].setTitleOrContent(vTitleTemplate.height);
            }
            return [textTree[0]];
        }
        const result1 = this.getShowResult(textTree[0], 0, vTitleTemplate);
        return result1;
    }

    private getShowResult(textResult: any[], depth = 0, vTitleTemplate?: VTitleTemplate): TextLeafNode[] {
        let showResult: TextLeafNode[] = [];
        let hasString = false;
        for (let i = 0; i < textResult.length; i++) {
            if (textResult[i] instanceof TextLeafNode) {
                hasString = true;
            }
        }
        for (let i = 0; i < textResult.length; i++) {
            if (textResult[i] instanceof TextLeafNode) {
                textResult[i].level = depth;
                if (vTitleTemplate) {
                    textResult[i].setTitleOrContent(vTitleTemplate.height);
                }
                showResult.push(textResult[i]);
            }
            if (Array.isArray(textResult[i])) {
                showResult = showResult.concat(this.getShowResult(textResult[i], hasString ? depth + 1 : depth));
            }
        }
        return showResult;
    }

    private getVIndexTitleTemplateMap(
        vTitleTemplateMap: Map<VTitleTemplate, Array<FirstAlignArea>>,
        vTitleTemplateSimilarityMap: Map<VTitleTemplate, Array<number>>,
    ): Map<number, VTitleTemplate> {
        const vIndexTitleTemplateMap: Map<number, VTitleTemplate> = new Map();
        vTitleTemplateMap.forEach((array, template) => {
            array.forEach((area) => {
                vIndexTitleTemplateMap.set(area.slideIndex, template);
            });
        });
        vTitleTemplateSimilarityMap.forEach((array, template) => {
            array.forEach((number) => {
                vIndexTitleTemplateMap.set(number, template);
            });
        });
        return vIndexTitleTemplateMap;
    }

    // 处理有目录页面的情况
    // eslint-disable-next-line complexity
    private handleHasTableContent(
        pageTexts: PageText[],
        tablesPageIndex: number,
        titleResult: {
            titleTemplateMap: Map<VTitleTemplate, Array<FirstAlignArea>>,
            titleTemplateSimilarMap: Map<VTitleTemplate, Array<number>>,
        },
    ) {
        const templateKeys = Array.from(titleResult.titleTemplateMap.keys());
        let maxSimilarNumber = 0;
        let bestTemplatePageIndexArray;
        for (let i = 0; i < templateKeys.length; i++) {
            const currentKey = templateKeys[i];
            const templatePageIndexArray = titleResult.titleTemplateMap.get(currentKey)
                ?.map(area => area.slideIndex)
                ?.concat((titleResult.titleTemplateSimilarMap.get(currentKey) || []));
            if (!templatePageIndexArray || templatePageIndexArray.length === 0) continue;
            // 有目录页，并且目录页的内容包含其他页的标题，推测为章节页
            let similarNumber = 0;
            const tablesPageString = pageTexts[tablesPageIndex].getAllTextString();
            for (let i = 0; i < templatePageIndexArray.length; i++) {
                const pageString = pageTexts[templatePageIndexArray[i]]?.getAllTextString() || '';
                if (this.longestCommonSubsequence(tablesPageString, pageString) / pageString.length < 2 / 3) {
                    similarNumber += 1;
                    break;
                }
            }
            if (similarNumber >= maxSimilarNumber) {
                maxSimilarNumber = similarNumber;
                bestTemplatePageIndexArray = templatePageIndexArray;
            }
        }
        if (maxSimilarNumber !== 0 && bestTemplatePageIndexArray) {
            bestTemplatePageIndexArray.forEach((index) => {
                if (index !== tablesPageIndex && pageTexts[index]) {
                    pageTexts[index].pageType = PageType.SectionDivider;
                }
            });
        }
    }

    // 处理没有目录页面的情况
    // eslint-disable-next-line complexity
    private handleNoTableContent(
        pageTexts: PageText[],
        titleResult: {
            titleTemplateMap: Map<VTitleTemplate, Array<FirstAlignArea>>,
            titleTemplateSimilarMap: Map<VTitleTemplate, Array<number>>,
        },
    ) {
        const templateKeys = Array.from(titleResult.titleTemplateMap.keys());
        let bestTemplatePageLength = 0;
        let bestTemplatePageIndexArray;
        for (let i = 0; i < templateKeys.length; i++) {
            const currentKey = templateKeys[i];
            const templatePageIndexArray = titleResult.titleTemplateMap.get(currentKey)
                ?.map(area => area.slideIndex)
                ?.concat((titleResult.titleTemplateSimilarMap.get(currentKey) || []));
            if (!templatePageIndexArray || templatePageIndexArray.length === 0) continue;
            // 页面内字数都小于40个字，并且都有数字
            let allCheck = true;
            for (let i = 0; i < templatePageIndexArray.length; i++) {
                const pageString = pageTexts[templatePageIndexArray[i]]?.getAllTextString() || '';
                if (pageString.length > 40 && !(/\d/).test(pageString)) {
                    allCheck = false;
                    break;
                }
            }
            if (!allCheck) continue;
            if (bestTemplatePageLength === 0 || templatePageIndexArray.length >= bestTemplatePageLength) {
                bestTemplatePageLength = templatePageIndexArray.length;
                bestTemplatePageIndexArray = templatePageIndexArray;
            }
        }
        if (bestTemplatePageLength !== 0 && bestTemplatePageIndexArray) {
            bestTemplatePageIndexArray.forEach((index) => {
                if (pageTexts[index]) {
                    pageTexts[index].pageType = PageType.SectionDivider;
                }
            });
        }
    }

    /**
     * 最长公共子序列
     * @param text1
     * @param text2
     * @private
     */
    private longestCommonSubsequence(text1: string, text2: string): number {
        /**
         dp[i][j]: text1中前i-1个和text2中前j-1个，最长公共子序列的长度
         */
        const length1: number = text1.length;
        const length2: number = text2.length;
        const dp: number[][] = new Array(length1 + 1).fill(0)
            .map(() => new Array(length2 + 1).fill(0));
        for (let i = 1; i <= length1; i++) {
            for (let j = 1; j <= length2; j++) {
                if (text1[i - 1] === text2[j - 1]) {
                    dp[i][j] = dp[i - 1][j - 1] + 1;
                } else {
                    dp[i][j] = Math.max(dp[i][j - 1], dp[i - 1][j]);
                }
            }
        }
        return dp[length1][length2];
    }

    private isContentsRef(text: string): boolean {
        const reg = /^目\s*录$/;

        return text.startsWith('contents') || reg.test(text) || text.endsWith('contents') || text.endsWith('content');
    }

    private isContentsListRef(texts: string[]): boolean {
        let firstText = false;
        let secondText = false;
        const reg = /^目$/;
        const reg2 = /^录$/;
        texts.forEach((text) => {
            if (reg.test(text.replaceAll(' ', ''))) {
                firstText = true;
            }

            if (reg2.test(text.replaceAll(' ', ''))) {
                secondText = true;
            }
        });

        return firstText && secondText;
    }
}
